import { useLight } from "@Light/services/light";
import type { AddressSearchResponse } from "@Light/services/lightTypes";
import { useMutation } from "@Light/utils/mutation";
import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";

export const MIN_QUERY_LENGTH = 3;

export type UseAddressSearch = {
  /** The current value of the search input. */
  query: string;
  /** Debounced handler for input value changes. */
  onInputChange: (value: string) => void;
  results: AddressSearchResponse[];
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  /** Whether the results contain duplicate addresses (different ESI IDs). */
  multipleSameAddress: boolean;
  /** Resets input and results */
  reset: () => void;
};

/** Hook that powers `AddressSearchField` */
export function useAddressSearch({
  postalCode,
}: { postalCode?: string } = {}): UseAddressSearch {
  const { useSearchAddressMutation } = useLight();
  const searchAddress = useMutation<
    { query: string; postal_code?: string },
    AddressSearchResponse[]
  >(useSearchAddressMutation);

  const [query, setQuery] = useState("");
  const [searchedQuery, setSearchedQuery] = useState("");

  const debouncedSearch = useDebouncedCallback((query: string) => {
    setSearchedQuery(query);
    searchAddress.mutate({ query, postal_code: postalCode });
  }, 300);

  const onInputChange = (newQuery: string) => {
    setQuery(newQuery);
    if (newQuery.length >= MIN_QUERY_LENGTH) {
      debouncedSearch(newQuery);
    }
  };

  const addresses = searchAddress.data;
  const hasAddresses = !!addresses && addresses.length > 0;
  const showAddresses =
    hasAddresses &&
    (searchedQuery === query || query.startsWith(searchedQuery));

  const isLoading = Boolean(searchAddress.isLoading);
  const isError = Boolean(searchAddress.isError);
  const isEmpty = !isError && addresses?.length === 0;

  const seenAddresses = new Set<string>();
  const multipleSameAddress = Boolean(
    addresses?.some((result) => {
      const addressStr = `${result.alias ?? result.address_1}, ${result.address_2}`;
      if (seenAddresses.has(addressStr)) {
        return true;
      }
      seenAddresses.add(addressStr);
      return false;
    }),
  );

  const reset = () => {
    setQuery("");
    setSearchedQuery("");
    searchAddress.reset();
  };

  return {
    query,
    onInputChange,
    results: showAddresses ? (addresses ?? []) : [],
    isLoading,
    isError,
    isEmpty,
    multipleSameAddress,
    reset,
  };
}
